import { useEffect, useState, Fragment } from 'react';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, Play } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { bankFeedHealthAPI, schedulerAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import { SchedulerHeartbeatLine } from './SchedulerJobDurationsPanel';

/**
 * Bank Feed Health (Diagnostics -> Integration Health).
 *
 * The Group Dashboard badge says "N feeds stale, oldest Nd ago". This card
 * says WHY, one verdict per bank-feed source, ordered by how fundamental
 * the cause is (scheduler down > sync job never registered > job failing >
 * consent expired > job not firing), with the stale feeds under each and
 * the one action that fixes it. Backend: routes/admin_bank_feed_health.py,
 * services/bank_feed_health.py. The hourly watchdog
 * (scheduler.scheduled_bank_feed_watchdog) alerts on the same verdicts.
 */

const VERDICT_BADGE = {
  healthy: { label: 'healthy', variant: 'default' },
  scheduler_down: { label: 'scheduler down', variant: 'destructive' },
  job_not_registered: { label: 'job not registered', variant: 'destructive' },
  job_failing: { label: 'job failing', variant: 'destructive' },
  credentials_or_consent: { label: 'credentials / consent', variant: 'destructive' },
  sync_erroring: { label: 'sync erroring', variant: 'destructive' },
  consent_expired: { label: 'consent expired', variant: 'destructive' },
  job_not_firing: { label: 'job not firing', variant: 'secondary' },
  stale_unexplained: { label: 'stale, cause unclear', variant: 'secondary' },
  unknown_source: { label: 'unmapped source', variant: 'outline' },
};

function fmtTimestamp(iso) {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB');
}

function fmtAgeHours(h) {
  if (h === null || h === undefined) return 'never';
  if (h < 1) return `${Math.round(h * 60)}m ago`;
  if (h < 48) return `${h.toFixed(1)}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function fmtInterval(seconds) {
  if (!seconds) return 'cron';
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

function SourceRow({ s, onSync, syncing }) {
  const [open, setOpen] = useState(s.stale_count > 0);
  const badge = VERDICT_BADGE[s.verdict] || { label: s.verdict, variant: 'outline' };
  const canSync = Boolean(s.sync_endpoint) || Boolean(s.job_registered && s.job_id);
  return (
    <Fragment>
      <tr
        className={`border-b ${s.verdict !== 'healthy' ? 'bg-destructive/5' : ''}`}
        data-testid={`bank-feed-source-${s.source}`}
      >
        <td className="px-2 py-1 whitespace-nowrap align-top">
          <button
            type="button"
            className="text-left font-medium hover:underline"
            onClick={() => setOpen(!open)}
            data-testid={`bank-feed-toggle-${s.source}`}
          >
            {s.label}
          </button>
          <div className="text-muted-foreground">
            {s.stale_count} of {s.feed_count} stale
            {s.oldest_last_synced ? ` · oldest ${fmtTimestamp(s.oldest_last_synced)}` : ''}
          </div>
        </td>
        <td className="px-2 py-1 align-top">
          <Badge variant={badge.variant} data-testid={`bank-feed-verdict-${s.source}`}>{badge.label}</Badge>
          <div className="mt-1 text-muted-foreground max-w-md" data-testid={`bank-feed-reason-${s.source}`}>{s.reason}</div>
          {s.operator_action && (
            <div className="mt-1 max-w-md">
              <span className="font-medium">Next: </span>{s.operator_action}
            </div>
          )}
        </td>
        <td className="px-2 py-1 whitespace-nowrap align-top">
          {s.job_id ? (
            <div>
              <div className="font-mono">{s.job_id}</div>
              <div className="text-muted-foreground">
                {s.job_registered ? `registered · every ${fmtInterval(s.job_interval_seconds)}` : 'NOT registered'}
              </div>
              <div className="text-muted-foreground">
                last run: {s.job_last_run ? `${fmtTimestamp(s.job_last_run.started_at)} (${s.job_last_run.outcome})` : 'never'}
              </div>
              {s.sync_last_error && (
                <div className="text-destructive max-w-xs whitespace-normal">sync: {s.sync_last_error}</div>
              )}
            </div>
          ) : '-'}
        </td>
        <td className="px-2 py-1 whitespace-nowrap align-top">
          {canSync && (
            <Button
              size="sm"
              variant="outline"
              disabled={syncing === s.source}
              onClick={() => onSync(s)}
              data-testid={`bank-feed-sync-${s.source}`}
            >
              {syncing === s.source ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />}
              Sync now
            </Button>
          )}
        </td>
      </tr>
      {open && s.feeds.length > 0 && (
        <tr className="border-b bg-muted/20" data-testid={`bank-feed-feeds-${s.source}`}>
          <td colSpan={4} className="px-2 py-1">
            <table className="w-full text-xs">
              <tbody>
                {s.feeds.map((f) => (
                  <tr key={f.account_id} className={f.stale ? 'text-destructive' : 'text-muted-foreground'}>
                    <td className="px-2 py-0.5 whitespace-nowrap">{f.entity}</td>
                    <td className="px-2 py-0.5 whitespace-nowrap">{f.bank} · {f.currency}</td>
                    <td className="px-2 py-0.5 whitespace-nowrap">{fmtAgeHours(f.age_hours)}</td>
                    <td className="px-2 py-0.5 whitespace-nowrap">
                      {f.consent_expired ? <Badge variant="destructive">consent expired</Badge> : (f.stale ? 'stale' : 'fresh')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

export function BankFeedHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data: payload } = await bankFeedHealthAPI.get();
      setData(payload);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSync = async (s) => {
    setSyncing(s.source);
    try {
      if (s.sync_endpoint) {
        await bankFeedHealthAPI.triggerSync(s.sync_endpoint);
        toast.success(`${s.label}: sync started`);
      } else {
        const { data: res } = await schedulerAPI.triggerJob(s.job_id);
        toast.success(res?.message || `${s.label}: job queued`);
      }
      // Give a direct sync a moment to stamp last_synced before re-reading.
      setTimeout(refresh, 2500);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSyncing(null);
    }
  };

  const totals = data?.totals;
  const unhealthy = (data?.sources || []).filter((s) => s.verdict !== 'healthy');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          Bank Feed Health
          {data && totals && totals.stale > 0 && (
            <Badge variant="destructive" data-testid="bank-feed-stale-badge">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {totals.stale} of {totals.feeds} feeds stale
            </Badge>
          )}
          {data && totals && totals.stale === 0 && (
            <Badge variant="default" data-testid="bank-feed-ok-badge">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              all {totals.feeds} feeds fresh
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Why each bank-feed source is stale (same feeds and threshold as the Group Dashboard badge),
          most fundamental cause first. The hourly Bank Feed Watchdog alerts on these verdicts and
          re-queues a registered job that stopped firing; anything needing a credential or a
          re-consent is a human step and is named as such.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Button size="sm" variant="outline" onClick={refresh} disabled={loading} data-testid="bank-feed-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Refresh
          </Button>
          {data && (
            <SchedulerHeartbeatLine
              source={data.scheduler && data.scheduler.present !== undefined ? 'heartbeat' : 'local'}
              heartbeat={data.scheduler}
            />
          )}
        </div>

        {data && data.sources.length === 0 && (
          <div className="text-xs text-muted-foreground" data-testid="bank-feed-empty">No active bank feeds.</div>
        )}

        {data && data.sources.length > 0 && (
          <div className="overflow-x-auto border rounded-lg" data-testid="bank-feed-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-2 py-1 font-medium">Source</th>
                  <th className="text-left px-2 py-1 font-medium">Verdict</th>
                  <th className="text-left px-2 py-1 font-medium">Scheduler job</th>
                  <th className="text-left px-2 py-1 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {[...unhealthy, ...data.sources.filter((s) => s.verdict === 'healthy')].map((s) => (
                  <SourceRow key={s.source} s={s} onSync={onSync} syncing={syncing} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
